/* Quiz funnel: questions, scoring, the email gate and the confirmation screen.

   The page renders one screen at a time into a single stage element. Option
   glyphs and every string arrive from the section through [data-quiz-config], so
   nothing customer-facing is written here. The email gate is a Shopify customer
   form authored in Liquid and cloned out of a <template>, because the POST is
   native: the page reloads, which is why the answers are mirrored into
   sessionStorage before submit and read back on the confirmation render. */
(() => {
  const root = document.querySelector('[data-quiz]');
  if (!root) return;

  const configEl = document.querySelector('[data-quiz-config]');
  if (!configEl) return;

  const config = JSON.parse(configEl.textContent);
  const stage = root.querySelector('[data-quiz-stage]');
  const backBtn = root.querySelector('[data-quiz-back]');
  const meter = root.querySelector('[data-quiz-meter]');
  const pipsEl = root.querySelector('[data-quiz-pips]');
  const countEl = root.querySelector('[data-quiz-count]');
  const gateTpl = root.querySelector('[data-quiz-gate-template]');
  const S = config.strings;
  const IMG = config.images;
  const ICON = config.icons || {};

  const STORE_KEY = 'kanva:quiz-answers';
  const MAIL_KEY = 'kanva:quiz-email';
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)');

  const QUESTIONS = [
    {
      id: 'intro',
      type: 'intro',
      eyebrow: S.introEyebrow,
      title: 'Find the ritual that fits <em>your</em> day.',
      body: 'A few questions about sleep, energy, and how you feel. Then a clear next step.',
      image: IMG.intro,
    },
    {
      id: 'your_goals',
      type: 'multiple',
      title: 'What are your goals?',
      sub: S.selectAll,
      options: [
        { value: 'lose_weight', label: 'Lose weight', icon: 'scale' },
        { value: 'reduce_symptoms', label: 'Reduce perimenopause, menopause, or postmenopause symptoms', icon: 'leaf' },
        { value: 'imrpove_well_being', label: 'Improve overall well-being', icon: 'heart' },
        { value: 'prefer_not_to_say', label: 'Prefer not to say', icon: 'eye-off', dominant: true },
      ],
    },
    {
      id: 'hot_flashes',
      type: 'single',
      title: 'Have you been experiencing hot flashes?',
      sub: S.selectOne,
      options: [
        { value: 'yes', label: 'Yes', icon: 'thermometer-sun' },
        { value: 'no', label: 'No', icon: 'minus' },
        { value: 'prefer_not_to_say', label: 'Prefer not to say', icon: 'eye-off', dominant: true },
      ],
    },
    {
      id: 'night_sweats',
      type: 'single',
      title: 'Do you have night sweats?',
      sub: S.selectOne,
      options: [
        { value: 'yes', label: 'Yes', icon: 'droplets' },
        { value: 'no', label: 'No', icon: 'minus' },
        { value: 'prefer_not_to_say', label: 'Prefer not to say', icon: 'eye-off', dominant: true },
      ],
    },
    {
      id: 'sleep_quality',
      type: 'single',
      title: 'How would you describe your sleep quality lately?',
      sub: S.selectOne,
      options: [
        { value: 'sleep_well', label: 'I sleep well most nights', icon: 'moon-star' },
        { value: 'restless_nights', label: 'I have some restless nights', icon: 'bed' },
        { value: 'wake_up_often', label: "I wake up often or don't feel rested", icon: 'cloud-moon' },
        { value: 'struggle_to_sleep', label: 'I struggle to sleep at all', icon: 'clock' },
        { value: 'prefer_not_to_say', label: 'Prefer not to say', icon: 'eye-off', dominant: true },
      ],
    },
    {
      id: 'intimacy_interest',
      type: 'single',
      title: 'Have you noticed any changes in your interest in intimacy?',
      sub: S.selectOne,
      options: [
        { value: 'same_interest', label: 'No change, my interest is the same', icon: 'heart' },
        { value: 'less_interest', label: 'I have less interest than before', icon: 'heart-crack' },
        { value: 'more_interest', label: 'I have more interest than before', icon: 'heart-pulse' },
        { value: 'prefer_not_to_say', label: 'Prefer not to say', icon: 'eye-off', dominant: true },
      ],
    },
    {
      id: 'often_tired_and_exhausted',
      type: 'single',
      title: 'Do you often feel tired, weak or even exhausted?',
      sub: S.selectOne,
      options: [
        { value: 'yes', label: 'Yes', icon: 'battery-low' },
        { value: 'no', label: 'No', icon: 'minus' },
        { value: 'prefer_not_to_say', label: 'Prefer not to say', icon: 'eye-off', dominant: true },
      ],
    },
    {
      id: 'often_anxious_or_overwhelmed',
      type: 'single',
      title: 'Do you often feel anxious or overwhelmed?',
      sub: S.selectOne,
      options: [
        { value: 'yes', label: 'Yes', icon: 'cloud-rain' },
        { value: 'no', label: 'No', icon: 'minus' },
        { value: 'prefer_not_to_say', label: 'Prefer not to say', icon: 'eye-off', dominant: true },
      ],
    },
    {
      id: 'anything_else_you_been_struggling',
      type: 'multiple',
      title: "Anything else you've been struggling with lately?",
      sub: 'The additional symptoms will help us understand your condition better',
      options: [
        { value: 'bloating', label: 'Bloating', icon: 'wind' },
        { value: 'sugar_or_carbs_cravings', label: 'Sugar or carbs cravings', icon: 'cookie' },
        { value: 'brain_fog_forgetfulness', label: 'Brain fog, forgetfulness', icon: 'brain' },
        { value: 'bone_weakening_and_joint_pain', label: 'Bone weakening and joint pain', icon: 'bone' },
        { value: 'vaginal_dryness', label: 'Vaginal dryness', icon: 'leaf' },
        { value: 'experience_none_of_these', label: 'I experience none of these', icon: 'check', dominant: true },
        { value: 'prefer_not_to_say', label: 'Prefer not to say', icon: 'eye-off', dominant: true },
      ],
    },
    {
      id: 'gained_weight',
      type: 'single',
      title: 'Have you gained weight in the last year?',
      sub: S.selectOne,
      options: [
        { value: 'yes', label: 'Yes', icon: 'scale' },
        { value: 'no', label: 'No', icon: 'minus' },
        { value: 'not_sure', label: 'Not sure', icon: 'circle-help' },
        { value: 'prefer_not_to_say', label: 'Prefer not to say', icon: 'eye-off', dominant: true },
      ],
    },
    {
      id: 'info_2',
      type: 'info',
      title: "You're in the right place.",
      body: 'Most people we hear from after 50 mention sleep, energy, or both. A short daily ritual is usually enough. Next we will match you to a ritual that fits.',
      image: IMG.evening,
    },
    {
      id: 'age',
      type: 'single',
      title: "What's your age?",
      sub: S.selectOne,
      options: [
        { value: 'up_to_35', label: 'Up to 35', icon: 'cake' },
        { value: '36-45', label: '36 - 45', icon: 'cake' },
        { value: '46-55', label: '46 - 55', icon: 'cake' },
        { value: '56-65', label: '56 - 65', icon: 'cake' },
        { value: '66+', label: '66+', icon: 'cake' },
      ],
    },
  ];

  const COUNTED = QUESTIONS.filter((q) => q.type !== 'intro' && q.type !== 'info');

  /* ----- State. `view` owns the screen, `index` only walks QUESTIONS ----- */
  let answers = {};
  let index = 0;
  let view = 'quiz';
  let timer = 0;
  let frame = 0;
  let touched = false;

  /* Mirrored into both stores. sessionStorage is what carries the answers
     across the gate POST; localStorage is what lets someone reopen their report
     later instead of retaking the quiz to see it again. */
  function save() {
    const raw = JSON.stringify(answers);
    try {
      window.sessionStorage.setItem(STORE_KEY, raw);
    } catch (error) {
      /* Private browsing: the quiz still works, it just cannot survive the POST. */
    }
    try {
      window.localStorage.setItem(STORE_KEY, raw);
    } catch (error) {
      /* Same again. The session copy is the one that matters. */
    }
  }

  function restore() {
    const read = (store) => {
      try {
        const raw = window[store].getItem(STORE_KEY);
        const data = raw ? JSON.parse(raw) : null;
        return data && typeof data === 'object' && Object.keys(data).length ? data : null;
      } catch (error) {
        return null;
      }
    };
    const found = read('sessionStorage') || read('localStorage');
    if (found) answers = found;
    return !!found;
  }

  function forget() {
    ['sessionStorage', 'localStorage'].forEach((store) => {
      try {
        window[store].removeItem(STORE_KEY);
      } catch (error) {
        /* Nothing to clean up. */
      }
    });
  }

  /* ----- Chrome: pips, counter, back ----- */
  function countedIndex() {
    if (view !== 'quiz') return -1;
    return COUNTED.findIndex((q) => q.id === QUESTIONS[index].id);
  }

  function buildPips() {
    pipsEl.innerHTML = COUNTED.map(() => '<span class="kv4-quiz__pip"></span>').join('');
  }

  function updateChrome() {
    const ci = countedIndex();
    const onQuestion = ci >= 0;
    meter.hidden = !onQuestion;
    backBtn.hidden = !(view === 'gate' || (view === 'quiz' && index > 0));

    if (!onQuestion) return;
    countEl.textContent = S.step.replace('{{ current }}', String(ci + 1)).replace('{{ total }}', String(COUNTED.length));
    pipsEl.querySelectorAll('.kv4-quiz__pip').forEach((pip, i) => {
      pip.classList.toggle('is-done', i < ci);
      pip.classList.toggle('is-on', i === ci);
    });
  }

  /* ----- Answers ----- */
  function selectedFor(id) {
    const raw = answers[id];
    if (Array.isArray(raw)) return raw;
    if (raw) return [raw];
    return [];
  }

  function setAnswer(q, value) {
    if (q.type === 'multiple') {
      const opt = q.options.find((o) => o.value === value);
      let next = selectedFor(q.id);
      if (opt && opt.dominant) {
        next = next.includes(value) ? [] : [value];
      } else {
        next = next.filter((v) => {
          const o = q.options.find((x) => x.value === v);
          return !(o && o.dominant);
        });
        next = next.includes(value) ? next.filter((v) => v !== value) : next.concat(value);
      }
      answers[q.id] = next;
    } else {
      answers[q.id] = value;
    }
    save();
  }

  /* ----- Screens ----- */
  function icon(name) {
    return ICON[name] || '';
  }

  function renderOptions(q) {
    const selected = selectedFor(q.id);
    return `<div class="kv4-quiz__options" role="${q.type === 'multiple' ? 'group' : 'radiogroup'}">
      ${q.options
        .map((o, i) => {
          const on = selected.includes(o.value);
          const cls = ['kv4-quiz__opt', o.dominant ? 'kv4-quiz__opt--quiet' : '', on ? 'is-on' : '']
            .filter(Boolean)
            .join(' ');
          return `<button type="button" class="${cls}" data-opt="${o.value}" style="--i:${i}" aria-pressed="${on}">
            <span class="kv4-quiz__opt-icon">${icon(o.icon)}</span>
            <span class="kv4-quiz__opt-label">${o.label}</span>
            <span class="kv4-quiz__opt-tick">${icon('check')}</span>
          </button>`;
        })
        .join('')}
    </div>`;
  }

  function renderIntro(q) {
    return `<div class="kv4-quiz__screen kv4-quiz__screen--center">
      <figure class="kv4-quiz__hero kv4-quiz__hero--figure">
        <img src="${q.image}" alt="" width="720" height="900">
      </figure>
      <p class="kv4-quiz__eyebrow">${q.eyebrow}</p>
      <h1 class="kv4-quiz__title" tabindex="-1">${q.title}</h1>
      <p class="kv4-quiz__body">${q.body}</p>
      <div class="kv4-quiz__actions">
        <button type="button" class="kv-btn kv4-quiz__cta" data-quiz-next>${S.start}</button>
      </div>
    </div>`;
  }

  function renderInfo(q) {
    return `<div class="kv4-quiz__screen kv4-quiz__screen--center">
      <figure class="kv4-quiz__hero kv4-quiz__hero--figure">
        <img src="${q.image}" alt="" width="720" height="900">
      </figure>
      <h2 class="kv4-quiz__title" tabindex="-1">${q.title}</h2>
      <p class="kv4-quiz__body">${q.body}</p>
      <div class="kv4-quiz__actions">
        <button type="button" class="kv-btn kv4-quiz__cta" data-quiz-next>${S.continue}</button>
      </div>
    </div>`;
  }

  function renderQuestion(q) {
    const canGo = selectedFor(q.id).length > 0;
    return `<div class="kv4-quiz__screen">
      <h2 class="kv4-quiz__title kv4-quiz__title--q" tabindex="-1">${q.title}</h2>
      ${q.sub ? `<p class="kv4-quiz__sub">${q.sub}</p>` : ''}
      ${renderOptions(q)}
      ${
        q.type === 'multiple'
          ? `<div class="kv4-quiz__actions">
              <button type="button" class="kv-btn kv4-quiz__cta" data-quiz-next ${canGo ? '' : 'disabled'}>${S.continue}</button>
            </div>`
          : ''
      }
    </div>`;
  }

  /* Counts up while the score is worked out, so the wait reads as progress
     rather than a spinner. */
  function renderAnalyze() {
    stage.innerHTML = `<div class="kv4-quiz__screen kv4-quiz__screen--center kv4-quiz__analyze">
      <figure class="kv4-quiz__hero kv4-quiz__hero--figure">
        <img src="${IMG.morning}" alt="" width="720" height="900">
      </figure>
      <p class="kv4-quiz__eyebrow">${S.almost}</p>
      <h2 class="kv4-quiz__title" tabindex="-1">${S.analyzing}</h2>
      <p class="kv4-quiz__status" data-quiz-status role="status" aria-live="polite">${S.analyzingSteps[0]}</p>
      <div class="kv4-quiz__gauge" aria-hidden="true"><span data-quiz-bar></span></div>
      <p class="kv4-quiz__percent" data-quiz-percent aria-hidden="true"></p>
    </div>`;

    const span = duration(1600);
    const bar = stage.querySelector('[data-quiz-bar]');
    const pct = stage.querySelector('[data-quiz-percent]');
    const status = stage.querySelector('[data-quiz-status]');
    const started = performance.now();

    const tick = (now) => {
      const ratio = Math.min(1, (now - started) / span);
      const value = Math.round(ratio * 100);
      if (bar) bar.style.transform = `scaleX(${ratio})`;
      if (pct) pct.textContent = S.percent.replace('{{ value }}', String(value));
      const line = S.analyzingSteps[Math.min(S.analyzingSteps.length - 1, Math.floor(ratio * S.analyzingSteps.length))];
      if (status && status.textContent !== line) status.textContent = line;
      if (ratio < 1) {
        frame = window.requestAnimationFrame(tick);
        return;
      }
      view = 'gate';
      render();
    };
    frame = window.requestAnimationFrame(tick);
  }

  /* Scoring, profile matching and the report document all live in
     kanva-quiz-report.js. Guarded because the funnel has to keep working if
     that file ever fails to load: the visitor still reaches the gate, the email
     is still captured, and they get the short confirmation instead. */
  function reportData() {
    if (!window.KanvaQuizReport) return null;
    try {
      return window.KanvaQuizReport.build(answers);
    } catch (error) {
      return null;
    }
  }

  function reportCtx() {
    return {
      strings: S,
      icon: icon,
      products: config.products,
      homeUrl: config.homeUrl,
    };
  }

  /* The customer form carries no free-text field, so the answers travel as
     customer tags. Shopify only applies these when it creates the customer, so
     a repeat taker keeps the tags from their first run. */
  function tagFor(text) {
    return String(text)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function answerTags(data) {
    const base = data && window.KanvaQuizReport ? window.KanvaQuizReport.tags(data) : ['quiz', 'newsletter'];
    const tags = base.slice();
    Object.keys(answers).forEach((id) => {
      const values = Array.isArray(answers[id]) ? answers[id] : [answers[id]];
      values.forEach((value) => {
        if (value) tags.push(tagFor(`quiz ${id} ${value}`));
      });
    });
    return tags.join(',');
  }

  function renderGate() {
    stage.innerHTML = `<div class="kv4-quiz__screen kv4-quiz__screen--center">
      <span class="kv4-quiz__seal">${icon('mail') || ''}</span>
      <p class="kv4-quiz__eyebrow">${S.gateEyebrow}</p>
      <h2 class="kv4-quiz__title" tabindex="-1">${S.gateTitle}</h2>
      <p class="kv4-quiz__body">${S.gateBody}</p>
      <div class="kv4-quiz__gate-slot" data-quiz-gate-slot></div>
    </div>`;

    const slot = stage.querySelector('[data-quiz-gate-slot]');
    if (!slot || !gateTpl) return;
    slot.appendChild(gateTpl.content.cloneNode(true));

    const tags = slot.querySelector('[data-quiz-tags]');
    if (tags) tags.value = answerTags(reportData());

    /* The POST is a real navigation - Shopify's form protection rules out
       submitting it in the background - so the answers are written out one last
       time on the way to reload, along with the address.

       Deliberately no preventDefault anywhere in here. The store runs hCaptcha
       on customer forms, and the token is attached to the native submit: taking
       the event over and calling form.submit() later loses it and the address
       is never captured. So nothing is sent from this handler. The Klaviyo call
       waits until the page has come back, where no navigation can cut it off. */
    const form = slot.querySelector('form');
    if (!form) return;
    form.addEventListener('submit', () => {
      save();
      const field = form.querySelector('[name="contact[email]"]');
      try {
        window.sessionStorage.setItem(MAIL_KEY, field ? field.value : '');
      } catch (error) {
        /* Without this the profile cannot be attached to anyone, but the
           address still reaches Shopify through the form itself. */
      }
    });
  }

  /* Klaviyo already runs on this page, and it is the only capture path that
     works for an existing customer: Shopify drops contact[tags] unless it is
     creating the customer, so a past purchaser would otherwise land with an
     email and no profile. Fired once, after the reload, and only if an address
     came back with the answers. */
  function identify(data) {
    let email = '';
    try {
      email = window.sessionStorage.getItem(MAIL_KEY) || '';
    } catch (error) {
      return;
    }
    if (!email || !data || !window.KanvaQuizReport) return;

    const props = window.KanvaQuizReport.profileProps(data, answers);
    try {
      window.klaviyo = window.klaviyo || [];
      /* The array form, which both the current object API and the older
         _learnq queue understand. */
      window.klaviyo.push(['identify', Object.assign({ $email: email }, props)]);
      window.klaviyo.push(['track', 'Completed Quiz', props]);
    } catch (error) {
      /* Blocked script or consent tooling. The report still renders. */
    }
    try {
      window.sessionStorage.removeItem(MAIL_KEY);
    } catch (error) {
      /* Nothing to clean up. */
    }
  }

  /* ----- Generating the report -----
     Runs after the gate POST has come back. The work itself is instant, so this
     screen exists to show what was done with the answers and to let the reveal
     land rather than flash. Each line ticks off in turn. */
  function renderGenerate() {
    const steps = S.genSteps || [];
    stage.innerHTML = `<div class="kv4-quiz__screen kv4-quiz__screen--center kv4-quiz__analyze">
      <span class="kv4-quiz__seal">${icon('mail')}</span>
      <p class="kv4-quiz__eyebrow">${S.genEyebrow}</p>
      <h2 class="kv4-quiz__title" tabindex="-1">${S.genTitle}</h2>
      <ul class="kv4-quiz__checks">
        ${steps
          .map(
            (label, i) => `<li class="kv4-quiz__check" data-gen-step="${i}">
              <span class="kv4-quiz__check-box">${icon('check')}</span>
              <span>${label}</span>
            </li>`
          )
          .join('')}
      </ul>
      <div class="kv4-quiz__gauge" aria-hidden="true"><span data-quiz-bar></span></div>
      <p class="kv4-quiz__percent" data-quiz-percent aria-hidden="true"></p>
    </div>`;

    const span = duration(3200);
    const bar = stage.querySelector('[data-quiz-bar]');
    const pct = stage.querySelector('[data-quiz-percent]');
    const rows = Array.prototype.slice.call(stage.querySelectorAll('[data-gen-step]'));
    const started = performance.now();

    const tick = (now) => {
      const ratio = Math.min(1, (now - started) / span);
      if (bar) bar.style.transform = `scaleX(${ratio})`;
      if (pct) pct.textContent = S.percent.replace('{{ value }}', String(Math.round(ratio * 100)));
      rows.forEach((row, i) => {
        row.classList.toggle('is-on', ratio >= (i + 1) / (rows.length + 0.35));
      });
      if (ratio < 1) {
        frame = window.requestAnimationFrame(tick);
        return;
      }
      view = 'report';
      render();
    };
    frame = window.requestAnimationFrame(tick);
  }

  function renderReport() {
    const data = reportData();
    if (!data) return false;
    stage.innerHTML = window.KanvaQuizReport.render(data, reportCtx());
    identify(data);
    return true;
  }

  function productCard(key, lead) {
    const p = config.products[key];
    if (!p) return '';
    return `<article class="kv4-quiz__card ${lead ? 'kv4-quiz__card--lead' : ''}">
      <img src="${p.image}" alt="" width="640" height="640">
      <div class="kv4-quiz__card-body">
        ${lead ? '' : `<p class="kv4-quiz__also">${S.also}</p>`}
        <h3>${p.title}</h3>
        <p>${p.why}</p>
        <a class="kv-btn kv4-quiz__cta kv4-quiz__cta--inline" href="${p.url}">${S.shop}</a>
      </div>
    </article>`;
  }

  /* Fallback only. Shown when the report cannot be rebuilt after the POST,
     which means the answers did not survive storage - private browsing, or a
     visitor who landed on the confirmation URL directly. The email is already
     captured by this point, so this screen still closes the loop. */
  function renderDone() {
    const data = reportData();
    const plan = data ? data.plan : { primary: 'creatine', secondary: 'sleephit' };
    return `<div class="kv4-quiz__screen kv4-quiz__done">
      <span class="kv4-quiz__seal kv4-quiz__seal--ok">${icon('check')}</span>
      <p class="kv4-quiz__eyebrow">${S.doneEyebrow}</p>
      <h2 class="kv4-quiz__title" tabindex="-1">${S.doneTitle}</h2>
      <p class="kv4-quiz__body">${S.doneBody}</p>
      <p class="kv4-quiz__lead">${S.doneLead}</p>
      ${productCard(plan.primary, true)}
      ${plan.secondary ? productCard(plan.secondary, false) : ''}
      <p class="kv4-quiz__fine">${S.disclaimer}</p>
      <a class="kv-btn kv-btn--light kv4-quiz__home-btn" href="${config.homeUrl}">${icon('house') || ''}<span>${S.backHome}</span></a>
      <button type="button" class="kv4-quiz__restart" data-quiz-restart>${S.restart}</button>
    </div>`;
  }

  /* ----- Render loop ----- */
  function duration(ms) {
    return calm.matches ? Math.min(ms, 120) : ms;
  }

  function render() {
    window.clearTimeout(timer);
    window.cancelAnimationFrame(frame);

    if (view === 'analyze') {
      updateChrome();
      renderAnalyze();
      return;
    }
    if (view === 'generate') {
      updateChrome();
      renderGenerate();
      return;
    }
    if (view === 'report') {
      /* Falls through to the short confirmation if the answers were lost, so
         the visitor never lands on an empty screen. */
      if (!renderReport()) {
        view = 'done';
        render();
        return;
      }
      updateChrome();
      bind();
      focusTitle();
      window.scrollTo({ top: 0, behavior: 'auto' });
      if (window.KanvaQuizReport && window.KanvaQuizReport.animate) {
        window.KanvaQuizReport.animate(stage, calm.matches);
      }
      return;
    }
    if (view === 'gate') {
      renderGate();
      updateChrome();
      bind();
      focusTitle();
      return;
    }
    if (view === 'done') {
      stage.innerHTML = renderDone();
      updateChrome();
      bind();
      focusTitle();
      return;
    }

    const q = QUESTIONS[index];
    if (q.type === 'intro') stage.innerHTML = renderIntro(q);
    else if (q.type === 'info') stage.innerHTML = renderInfo(q);
    else stage.innerHTML = renderQuestion(q);
    updateChrome();
    bind();
    focusTitle();
  }

  /* Only after the visitor has acted: focusing on first paint would yank the
     page down before they have read anything. */
  function focusTitle() {
    if (!touched) return;
    stage.querySelector('.kv4-quiz__title')?.focus?.({ preventScroll: true });
  }

  function go(next) {
    touched = true;
    if (next >= QUESTIONS.length) {
      view = 'analyze';
      render();
      return;
    }
    index = Math.max(0, next);
    view = 'quiz';
    render();
  }

  function bind() {
    stage.querySelectorAll('[data-opt]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const q = QUESTIONS[index];
        setAnswer(q, btn.getAttribute('data-opt'));
        if (q.type !== 'single') {
          render();
          return;
        }
        /* Paint the selection, then move on: an instant jump hides the tick. */
        btn.classList.add('is-on');
        btn.setAttribute('aria-pressed', 'true');
        timer = window.setTimeout(() => go(index + 1), duration(220));
      });
    });

    stage.querySelector('[data-quiz-next]')?.addEventListener('click', () => go(index + 1));

    stage.querySelector('[data-quiz-restart]')?.addEventListener('click', () => {
      answers = {};
      forget();
      go(0);
    });
  }

  backBtn.addEventListener('click', () => {
    if (view === 'gate') {
      go(QUESTIONS.length - 1);
      return;
    }
    go(index - 1);
  });

  /* ----- Entry point. The gate POST reloads the page, so the outcome of the
     previous submit decides which screen opens. ----- */
  const posted = !!gateTpl?.content.querySelector('[data-quiz-posted]');
  const failed = !!gateTpl?.content.querySelector('[data-quiz-error]');

  buildPips();

  if (posted) {
    /* Only run the build-up when there is something to build from. Without the
       answers the report cannot be assembled, and watching a progress bar fill
       for three seconds to reach a one-line confirmation would be a worse
       screen than the confirmation on its own. */
    view = restore() ? 'generate' : 'done';
    touched = true;
  } else if (failed) {
    restore();
    view = 'gate';
    touched = true;
  }

  render();
})();
